import {
	Address,
	IShipmentService,
	Party,
	PosturinnSettingsKey,
	Shipment,
	UserRecipients,
} from '../@posturinn/client-react';
import {
	IMobizSettingsService,
	MobizSettings,
	MobizSettingsKey,
	MobizSettingsScope,
} from '../@posturinn/common-react/settings';
import { IStepExportFormInfo } from '../components/registerShipment/registerShipmentForm/stepExport/stepExport.schema';
import { IStepInfoFormInfo } from '../components/registerShipment/registerShipmentForm/stepInfo/stepInfo.schema';
import { IStepOptionalServicesFormInfo } from '../components/registerShipment/registerShipmentForm/stepOptionalServices/stepOptionalServices.schema';
import { IStepShipmentFormInfo } from '../components/registerShipment/registerShipmentForm/stepShipment/stepShipment.schema';
import { IStepTypeFormInfo } from '../components/registerShipment/registerShipmentForm/stepType/stepType.schema';
import {
	FETCH_RECIPIENTS_FAILED,
	FETCH_RECIPIENTS_FULFILLED,
	FETCH_RECIPIENTS_PENDING,
} from '../constants/actions/recipientsActions';
import {
	RESET_TO_INITIAL_VALUES,
	SET_COD_OPTIONAL_SERVICE_PRICE_FULFILLED,
	SET_INSURANCE_OPTIONAL_SERVICE_PRICE_FULFILLED,
	STEP_EXPORT_FULFILLED,
	STEP_FEEDBACK_FULFILLED,
	STEP_INFO_FULFILLED,
	STEP_OPTIONAL_SERVICES_FULFILLED,
	STEP_SHIPMENT_FULFILLED,
	STEP_SHIPMENT_SENDER_SSN_ADDED,
	STEP_TYPE_COUNTRY_ADDED,
	STEP_TYPE_FULFILLED,
	SUBMIT_DOMESTIC_SHIPMENT_FAILED,
	SUBMIT_DOMESTIC_SHIPMENT_FULFILLED,
	SUBMIT_DOMESTIC_SHIPMENT_PENDING,
	SUBMIT_INTERNATIONAL_SHIPMENT_FAILED,
	SUBMIT_INTERNATIONAL_SHIPMENT_FULFILLED,
	SUBMIT_INTERNATIONAL_SHIPMENT_PENDING,
	UPDATE_TOTAL_PRICE,
} from '../constants/actions/registerShipmentFormActions';
import { FETCH_SHIPMENT_FULFILLED } from '../constants/actions/shipmentsActions';
import { getInternationalShipmentDataModel } from '../helpers/apiHelpers/registerShipmentHelpers';
import { normalizeShipmentPhoneForDestination } from '../helpers/formHelpers/formHelpers';
import { CLIENT_ID } from '../helpers/mobizHelpers/configurations';
import { getCurrentLangPretty } from '../helpers/url/urlHelpers';
import { IDomesticShipmentReturnDataModel } from '../interfaces/apiModels/domesticShipmentDataModel';
import { IFormSelectOption } from '../interfaces/forms/forms';
import { IRegisterShipmentReducer } from '../reducers/registerShipmentReducer';
import store from '../store';
import { CREATE_RECIPIENT_FAILED, CREATE_RECIPIENT_FULFILLED } from './../constants/actions/recipientsActions';
import { closeAllModals } from './modalActions';
import { IReturn } from '../interfaces/returns/return';
import { take } from 'rxjs/operators';
import { datadogLogs } from '@datadog/browser-logs';

const getSubmissionErrorMessage = (error: any) =>
	error?.info?.details ||
	error?.response?.data?.details ||
	error?.response?.data?.message ||
	error?.message ||
	'';

export const stepTypeFulfilled = (values: IStepTypeFormInfo) => ({
	type: STEP_TYPE_FULFILLED,
	payload: {
		values,
	},
});

export const stepFeedbackFulfilled = (values: any) => ({
	type: STEP_FEEDBACK_FULFILLED,
	payload: {
		values,
	},
});

export const stepTypeCountryAdded = (country: IFormSelectOption) => ({
	type: STEP_TYPE_COUNTRY_ADDED,
	payload: {
		country,
	},
});

export const stepShipmentFulfilled = (values: IStepShipmentFormInfo) => ({
	type: STEP_SHIPMENT_FULFILLED,
	payload: {
		values,
	},
});

export const stepOptionalServicesFulfilled = (values: IStepOptionalServicesFormInfo) => ({
	type: STEP_OPTIONAL_SERVICES_FULFILLED,
	payload: {
		values,
	},
});

export const stepShipmentSenderSsnAdded = (values: string) => ({
	type: STEP_SHIPMENT_SENDER_SSN_ADDED,
	payload: {
		values,
	},
});

export const stepInfoFulfilled = (values: IStepInfoFormInfo) => ({
	type: STEP_INFO_FULFILLED,
	payload: {
		values,
	},
});

export const stepExportFulfilled = (values: IStepExportFormInfo) => ({
	type: STEP_EXPORT_FULFILLED,
	payload: {
		values,
	},
});

export const updateTotalPrice = (price: number) => ({
	type: UPDATE_TOTAL_PRICE,
	payload: {
		price,
	},
});

export const submitDomesticShipmentPending = () => ({
	type: SUBMIT_DOMESTIC_SHIPMENT_PENDING,
	payload: null,
});

export const submitDomesticShipmentFulfilled = (data: IDomesticShipmentReturnDataModel) => ({
	type: SUBMIT_DOMESTIC_SHIPMENT_FULFILLED,
	payload: {
		data,
	},
});

export const submitDomesticShipmentFailed = error => ({
	type: SUBMIT_DOMESTIC_SHIPMENT_FAILED,
	payload: {
		error,
	},
});

export const submitInternationalShipmentPending = () => ({
	type: SUBMIT_INTERNATIONAL_SHIPMENT_PENDING,
	payload: null,
});

export const submitInternationalShipmentFulfilled = (data: {
	registrationKey: string;
	shipmentId: string;
	customerId: string;
}) => ({
	type: SUBMIT_INTERNATIONAL_SHIPMENT_FULFILLED,
	payload: {
		data,
	},
});

export const submitInternationalShipmentFailed = error => ({
	type: SUBMIT_INTERNATIONAL_SHIPMENT_FAILED,
	payload: {
		error,
	},
});

export const submitInternationalShipment = (userId, isLoggedIn, recipients, history, chargeSenderCard, returnInfo: IReturn|false=false) => {
	return async (dispatch, getState) => {
		const { registerShipmentReducer }: { registerShipmentReducer: IRegisterShipmentReducer } = getState();
		const { stepInfoFormInfo } = registerShipmentReducer;
		const internationalShipmentDataModel = getInternationalShipmentDataModel(registerShipmentReducer);
		internationalShipmentDataModel.chargeSenderCard = chargeSenderCard;
		dispatch(submitInternationalShipmentPending());

		try {
			const { injector } = getState().mobizReducer;
			const shipmentService: IShipmentService = injector.get(IShipmentService);

			if (isLoggedIn)
				shipmentService.registerShipment(internationalShipmentDataModel).subscribe({
					next: newShipment => {
						dispatch({ type: FETCH_SHIPMENT_FULFILLED, payload: newShipment });
						if (returnInfo){
							shipmentService.sendReturnEmail(returnInfo, newShipment.id).pipe(take(1)).subscribe({
								error: (error) => {
									datadogLogs.logger.error('Send return email failed - Metta sport', {
										componentStack: error.componentStack
									}, error);
								},
							});
						}
						if (
							userId &&
							recipients &&
							(stepInfoFormInfo.updateRecipient || stepInfoFormInfo.saveRecipient)
						) {
							const recipient: Party = new Party();

							recipient.name = stepInfoFormInfo.receiverName;
							recipient.identificationNumber = stepInfoFormInfo.receiverSsn;
							recipient.email = stepInfoFormInfo.receiverEmail;
							recipient.phone = normalizeShipmentPhoneForDestination(
								stepInfoFormInfo.receiverPhone,
								stepInfoFormInfo.country.value
							);

							const address: Address = new Address();
							address.country = stepInfoFormInfo.country.value;

							if (stepInfoFormInfo.country.value === 'IS') {
								address.street1 = stepInfoFormInfo.receiverAddress;
								address.street2 = '';
								address.postalCode = stepInfoFormInfo.receiverPostCode.label.substring(0, 3) || '';
								address.city = stepInfoFormInfo.receiverPostCode.label.substring(4) || '';
							} else {
								address.street1 = stepInfoFormInfo.receiverAddress;
								address.street2 = stepInfoFormInfo.receiverForeignAddressExtended;
								address.postalCode = stepInfoFormInfo.receiverForeignPostCode;
								address.city = stepInfoFormInfo.receiverForeignPlace;
							}
							recipient.address = address;

							if (stepInfoFormInfo.saveRecipient)
								dispatch(createRecipient(userId, recipient, recipients));
							else if (stepInfoFormInfo.updateRecipient) {
								recipient.id = stepInfoFormInfo.recipient.value;
								dispatch(editRecipient(userId, recipient, recipients));
							}
						}
						// If the client is in a postoffice we don't want to redirect
						if (CLIENT_ID === 'posturinn-postoffice') {
							dispatch(
								submitInternationalShipmentFulfilled({
									registrationKey: newShipment.registrationKey,
									shipmentId: newShipment.id,
									customerId: newShipment.sender.identificationNumber,
								})
							);
						} else {
							history.push(
								getCurrentLangPretty() === 'is'
									? '/minar-sidur/sending?id=' + newShipment.id
									: '/en/my-account/shipment?id=' + newShipment.id
							);
							dispatch(resetToInitalValues());
						}
					},
					error: error => {
						dispatch(submitInternationalShipmentFailed(getSubmissionErrorMessage(error)));
					},
				});
			else {
				shipmentService.registerShipmentForAnonymous(internationalShipmentDataModel).subscribe({
					next: (newShipment: Shipment) => {
						dispatch(
							submitInternationalShipmentFulfilled({
								registrationKey: newShipment.registrationKey,
								shipmentId: newShipment.id,
								customerId: newShipment.sender.identificationNumber,
							})
						);
					},
					error: error => {
						dispatch(submitInternationalShipmentFailed(getSubmissionErrorMessage(error)));
					},
				});
			}
		} catch (error) {
			dispatch(submitInternationalShipmentFailed(getSubmissionErrorMessage(error)));
		}
	};
};

export const setInsuranceOptionalServicePrice = (price: number) => ({
	type: SET_INSURANCE_OPTIONAL_SERVICE_PRICE_FULFILLED,
	payload: {
		price,
	},
});

export const setCodOptionalServicePrice = (price: number) => ({
	type: SET_COD_OPTIONAL_SERVICE_PRICE_FULFILLED,
	payload: {
		price,
	},
});

export const resetToInitalValues = () => ({
	type: RESET_TO_INITIAL_VALUES,
	payload: null,
});

const settingsScope: MobizSettingsScope = 'user';
const recipientsSettingsKey: PosturinnSettingsKey = 'PosturinnUserRecipients';
let settings = new MobizSettings<UserRecipients>(UserRecipients);

export const getRecipients = (userId: string) => {
	return async dispatch => {
		dispatch({
			type: FETCH_RECIPIENTS_PENDING,
			payload: null,
		});
		try {
			const { injector } = store.getState().mobizReducer;
			const settingsService: IMobizSettingsService = injector.get(IMobizSettingsService);

			settingsService
				.get<UserRecipients>(
					settingsScope,
					userId,
					(recipientsSettingsKey as MobizSettingsKey),
					new MobizSettings<UserRecipients>(UserRecipients)
				)
				.subscribe({
					next: (value: MobizSettings<UserRecipients>) => {
						settings = value;
						dispatch({
							type: FETCH_RECIPIENTS_FULFILLED,
							payload: value.value.recipients,
						});
					},
					error: error => {
						dispatch({
							type: FETCH_RECIPIENTS_FAILED,
							payload: error,
						});
					},
				});
		} catch (error) {
			dispatch({
				type: FETCH_RECIPIENTS_FAILED,
				payload: error,
			});
		}
	};
};

const saveRecipients = (recipients: Party[], userId: string) => {
	return async dispatch => {
		try {
			const { injector } = store.getState().mobizReducer;
			const settingsService: IMobizSettingsService = injector.get(IMobizSettingsService);

			settings.value.recipients = recipients;

			settings.origin === 'DB'
				? settingsService.update(settingsScope, userId, recipientsSettingsKey, settings).subscribe({
						next: (value: MobizSettings<UserRecipients>) => {
							dispatch({
								type: CREATE_RECIPIENT_FULFILLED,
								payload: value.value.recipients,
							});
							dispatch(closeAllModals());
							document.body.style.overflow = 'unset';
						},
						error: error => {
							dispatch({
								type: CREATE_RECIPIENT_FAILED,
								payload: error,
							});
						},
				  })
				: settingsService.add(settingsScope, userId, recipientsSettingsKey, settings).subscribe({
						next: (value: MobizSettings<UserRecipients>) => {
							dispatch({
								type: CREATE_RECIPIENT_FULFILLED,
								payload: value.value.recipients,
							});
							dispatch(closeAllModals());
							document.body.style.overflow = 'unset';
						},
						error: error => {
							dispatch({
								type: CREATE_RECIPIENT_FAILED,
								payload: error,
							});
						},
				  });
		} catch (error) {
			dispatch({
				type: CREATE_RECIPIENT_FAILED,
				payload: error,
			});
		}
	};
};

export const createRecipient = (userId: string, recipient: Party, recipients: Party[]) => {
	return async dispatch => {
		if (!recipients) recipients = [];
		recipient.id = uuidv4();
		recipients.push(recipient);
		dispatch(saveRecipients(recipients, userId));
	};
};

export const editRecipient = (userId: string, recipient: Party, recipients: Party[]) => {
	return async dispatch => {
		const tempRecipients: Party[] = [];
		while (recipients.length > 0 && recipients[recipients.length - 1].id !== recipient.id)
			tempRecipients.push(recipients.pop());
		recipients.pop();
		recipients.push(recipient);
		recipients = recipients.concat(tempRecipients.reverse());
		dispatch(saveRecipients(recipients, userId));
	};
};

export const deleteRecipient = (userId: string, recipientId: string, recipients: Party[]) => {
	return async dispatch => {
		const newRecipients = recipients.filter((rec: Party) => rec.id !== recipientId);
		dispatch(saveRecipients(newRecipients, userId));
	};
};

const uuidv4 = () => {
	return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
		var r = (Math.random() * 16) | 0,
			v = c === 'x' ? r : (r & 0x3) | 0x8;
		return v.toString(16);
	});
};
